import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { selectFeatureFeed, selectFeedView } from '../ngrx-store/selectors/corp.selectors';
import { CallerService } from '../services/caller.service';
import { AppState, SaleType } from '../ngrx-store/app.state';
import { loadAllItems, loadAllItemsFailure } from '../ngrx-store/actions/corp-member.actions';
import { categoryLabel, locationState, nearness, SALE_CATEGORIES } from '../utils/sale.util';

/** The query params the filters live in, so a filtered page can be shared or bookmarked. */
const CATEGORY_PARAM = 'category'
const STATE_PARAM = 'state'

interface SaleFilters {
  categories: Array<{ value: string; label: string; count: number }>
  states: Array<{ value: string; count: number }>
  allCategories: number // "All categories": everything in the chosen state
  allStates: number // "All states": everything in the chosen category
  filtered: boolean
}

const stateOf = (sale: SaleType): string | undefined => locationState(sale.CorpMember?._location)

@Component({
  selector: 'app-sales-page',
  templateUrl: './sales-page.component.html'
})
export class SalesPageComponent implements OnInit, OnDestroy {
  readonly categories = SALE_CATEGORIES
  readonly skeletons = Array.from({ length: 8 })

  category$ = new BehaviorSubject<string | null>(null)
  state$ = new BehaviorSubject<string | null>(null)

  /** Every sale in the feed, newest first. */
  private sales$: Observable<SaleType[]> = this.store
    .select(selectFeedView('sale'))
    .pipe(map((sales) => [...sales.values()] as SaleType[]))

  /**
   * The two dropdowns' options, each with how many items it would show given the other filter ("Kitchen &
   * cooking · 12" counts kitchen items in the chosen state). An option with nothing is left out - except the
   * one that's picked, so it never vanishes from under you.
   */
  filters$: Observable<SaleFilters> = combineLatest([this.sales$, this.category$, this.state$]).pipe(
    map(([sales, category, state]) => {
      const inState = sales.filter((sale) => !state || stateOf(sale) === state)
      const inCategory = sales.filter((sale) => !category || sale.category === category)
      const stateCounts = new Map<string, number>()
      for (const sale of inCategory) {
        const s = stateOf(sale)
        if (s) stateCounts.set(s, (stateCounts.get(s) ?? 0) + 1)
      }
      if (state && !stateCounts.has(state)) stateCounts.set(state, 0)
      return {
        categories: this.categories
          .map((c) => ({ value: c.value as string, label: c.label as string, count: inState.filter((sale) => sale.category === c.value).length }))
          .filter((c) => c.count > 0 || c.value === category),
        states: [...stateCounts].map(([value, count]) => ({ value, count })).sort((a, b) => a.value.localeCompare(b.value)),
        allCategories: inState.length,
        allStates: inCategory.length,
        filtered: !!(category || state),
      }
    }),
  )

  /** What the grid shows: the filters applied, nearest to you first (same LGA, then same state). */
  shown$: Observable<SaleType[]> = combineLatest([this.sales$, this.category$, this.state$]).pipe(
    map(([sales, category, state]) => {
      const viewer = this.callerService.corpMember?._location
      return sales
        .filter((sale) => (!category || sale.category === category) && (!state || stateOf(sale) === state))
        // Array.sort is stable, so each group stays newest first.
        .sort((a, b) => nearness(b.CorpMember?._location, viewer) - nearness(a.CorpMember?._location, viewer))
    }),
  )

  hasAnySales$: Observable<boolean> = this.sales$.pipe(map((sales) => sales.length > 0))

  /** Until GET /all-items first answers (or fails) - the skeleton cards show meanwhile. */
  isFetching = true

  private destroy$ = new Subject<void>()

  constructor(
    public callerService: CallerService,
    public store: Store<AppState>,
    private actions$: Actions,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit() {
    this.store.dispatch(loadAllItems())

    this.store.select(selectFeatureFeed).pipe(takeUntil(this.destroy$)).subscribe((feed) => {
      if (feed.lastFetchedAt || feed.sales.size) this.isFetching = false
    })
    this.actions$.pipe(ofType(loadAllItemsFailure), takeUntil(this.destroy$)).subscribe(() => {
      this.isFetching = false
      this.callerService.showNotification('Couldn\'t load items for sale. Check your connection and try again.')
    })

    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.category$.next(params.get(CATEGORY_PARAM))
      this.state$.next(params.get(STATE_PARAM))
    })
  }

  ngOnDestroy() {
    this.destroy$.next()
    this.destroy$.complete()
  }

  setCategory(category: string | null) {
    this.setFilter(CATEGORY_PARAM, category)
  }

  setState(state: string | null) {
    this.setFilter(STATE_PARAM, state)
  }

  clearFilters() {
    this.router.navigate([], { relativeTo: this.route, queryParams: { [CATEGORY_PARAM]: null, [STATE_PARAM]: null }, queryParamsHandling: 'merge', replaceUrl: true })
  }

  private setFilter(param: string, value: string | null) {
    this.router.navigate([], { relativeTo: this.route, queryParams: { [param]: value }, queryParamsHandling: 'merge', replaceUrl: true })
  }

  openWishlist() {
    this.callerService.openOnboardingChecklist().subscribe()
  }

  categoryName(category: string | null): string {
    return categoryLabel(category) ?? 'All categories'
  }

  trackById = (_index: number, sale: SaleType) => sale.id
}
